import { AlertTriangle, Clock, ExternalLink, Info, RadioTower } from 'lucide-react'
import clsx from 'clsx'
import { formatDateTime } from '@/utils/format'
import { approvedWindow, runMayStillBeActive, safeExternalUrl } from '@/utils/patchStage'
import type { PatchStage } from '@/types/patching'
import styles from './Patching.module.css'

/** Why a stage ended in a bad state — a refused acceptance always carries its problems. */
function FailureNotice({ stage }: { stage: PatchStage }) {
  const failure = stage.failure
  const problems = stage.acceptance && stage.acceptance.passed === false ? (stage.acceptance.problems ?? []) : []
  const refused = stage.acceptance?.passed === false
  const shown = stage.status === 'failed' || !!failure || refused
  if (!shown) return null

  const reason = failure?.reason?.trim() || null
  const heading = failure?.stage
    ? `Failed at ${failure.stage}.`
    : refused && !reason
      ? 'Acceptance was refused by the portal.'
      : 'Stage failed.'

  return (
    <div className={clsx(styles.alert, styles.alertError)} data-testid="stage-failure">
      <AlertTriangle size={16} className={styles.alertIcon} />
      <div className={styles.stack} style={{ gap: 6 }}>
        <span>
          <strong>{heading}</strong> {reason}
          {failure?.retry_safe === true && ' Retrying is safe.'}
          {failure?.retry_safe === false && ' Retrying may not be safe — check the run first.'} Promotion to later environments is blocked.
        </span>
        {!reason && problems.length > 0 && (
          <ul className={styles.advisorList} data-testid="stage-failure-problems">
            {problems.map((problem, i) => (
              <li key={`${i}-${problem}`}>{problem}</li>
            ))}
          </ul>
        )}
        {!reason && problems.length === 0 && (
          <span data-testid="stage-failure-unrecorded">
            The portal has not recorded a reason for this stage. Check the workflow run and the audit trail.
          </span>
        )}
      </div>
    </div>
  )
}

/** Banners that explain the stage's current state: failure, silence, unconfirmed dispatch, cancel outcome, window. */
export function StageNotices({ stage }: { stage: PatchStage }) {
  const window = approvedWindow(stage)
  const cancelUrl = safeExternalUrl(stage.run_cancel?.run_url) ?? safeExternalUrl(stage.run_url)
  const mayBeActive = runMayStillBeActive(stage)

  return (
    <>
      <FailureNotice stage={stage} />

      {stage.status === 'rejected' && (
        <div className={clsx(styles.alert, styles.alertError)} data-testid="stage-rejected">
          <AlertTriangle size={16} className={styles.alertIcon} />
          <span>This environment was rejected by an approver. Promotion is blocked.</span>
        </div>
      )}

      {stage.stale && (
        <div className={clsx(styles.alert, styles.alertWarning)} data-testid="stage-stale">
          <Clock size={16} className={styles.alertIcon} />
          <span>
            <strong>No update from the pipeline since</strong>{' '}
            {stage.last_callback_at ? formatDateTime(stage.last_callback_at) : 'it was dispatched (nothing has been received)'}.
            The run may be stuck or lost — check the workflow run, and cancel the stage if it is not progressing.
          </span>
        </div>
      )}

      {stage.dispatch_unconfirmed && (
        <div className={clsx(styles.alert, styles.alertWarning)} data-testid="stage-dispatch-unconfirmed">
          <RadioTower size={16} className={styles.alertIcon} />
          <span>
            <strong>Dispatch not confirmed.</strong> GitHub did not confirm that the workflow started. Do not re-dispatch;
            cancel the stage if you are unsure.
          </span>
        </div>
      )}

      {mayBeActive && (
        <div className={clsx(styles.alert, styles.alertError)} role="alert" data-testid="run-cancel-warning" data-state={stage.run_cancel?.state}>
          <AlertTriangle size={16} className={styles.alertIcon} />
          <span>
            <strong>
              {stage.run_cancel?.state === 'failed'
                ? 'The portal could not cancel the GitHub run.'
                : 'The portal could not find the GitHub run to cancel.'}
            </strong>{' '}
            The GitHub run may still be active and could still change the VMs. Check it and stop it in GitHub if needed.
            {stage.run_cancel?.detail ? ` ${stage.run_cancel.detail}` : ''}
            {cancelUrl && (
              <>
                {' '}
                <a href={cancelUrl} target="_blank" rel="noopener noreferrer" data-testid="run-cancel-link">
                  <ExternalLink size={12} /> Open the run
                </a>
              </>
            )}
          </span>
        </div>
      )}
      {stage.run_cancel?.state === 'accepted' && (
        <div className={clsx(styles.alert, styles.alertInfo)} data-testid="run-cancel-accepted">
          <Info size={16} className={styles.alertIcon} />
          <span>GitHub accepted the request to cancel the run.</span>
        </div>
      )}

      {window && (
        <p className={styles.muted} data-testid="maintenance-window">
          Approved maintenance window {formatDateTime(window.start)} – {formatDateTime(window.end)} (shown in your local time)
        </p>
      )}
    </>
  )
}
